import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import styled from 'styled-components';
import Tree from 'components/Tree';
import { setSidePanel } from 'reducers/sidePanel/actions';
import { RESOURCE_TYPES, SIDEPANEL_STATES } from 'utils/appConstants';
import { useDispatch, useSelector } from 'react-redux';
import { isExternallyManaged } from 'utils/format';
import Table from './Table';

const externallyManagedEdevs =
  window.env.REACT_APP_EXTERNALLY_MANAGED_EDEVS === 'true';

const IndeNodeMapContainer = styled.div`
  display: flex;
  flex: 1;
  z-index: 1;
  height: 100%;
`;

const TableContainer = styled.div`
  border-right: 1px solid #cfd1d3;
  display: flex;
  width: 600px;
  height: 100%;
  min-width: 500px;
  overflow-y: auto;
  padding: 5px;
`;

const TreeContainer = styled.div`
  padding: 2.75em 1em;
`;

const IndeNodeMap = ({ onRef }) => {
  const indeNodes = useSelector(state => state.indeNode.indeNodes);
  const sidepanel = useSelector(state => state.sidePanel);
  const dispatch = useDispatch();
  const [selectedIndeNode, setSelectedIndeNode] = useState(null);
  const [selectedOnMap, setSelectedOnMap] = useState(false);
  const sidePanelIsOpen = sidepanel.displaySidePanel;
  const indeNodeTableRef = useRef(null);

  useEffect(() => {
    if (
      sidepanel.entity === RESOURCE_TYPES.INDENODE &&
      sidepanel.state === SIDEPANEL_STATES.READ &&
      sidepanel.id !== selectedIndeNode
    ) {
      setSelectedIndeNode(sidepanel.id);
      setSelectedOnMap(false);
    }
  }, [sidepanel.entity, sidepanel.state, sidepanel.id, selectedIndeNode]);

  const indeNodeColumnDefs = useMemo(() => {
    const columns = [
      {
        headerName: 'ID',
        field: 'indeNodeId'
      },
      {
        headerName: 'Description',
        field: 'description'
      }
    ];
    if (externallyManagedEdevs) {
      columns.push({
        headerName: 'Remotely Managed',
        field: 'localNode',
        cellRenderer: ({ value }) => isExternallyManaged(value),
        maxWidth: 120
      });
    }
    columns.push({
      headerName: 'Primacy',
      field: 'primacy'
    });
    return columns;
  }, []);

  const onSelectIndeNode = useCallback(
    row => {
      const { indeNodeId } = row.data;
      dispatch(
        setSidePanel({
          entity: RESOURCE_TYPES.INDENODE,
          state: SIDEPANEL_STATES.READ,
          id: indeNodeId,
          selectedOnTreeMap: true
        })
      );
      setSelectedIndeNode(indeNodeId);
      setSelectedOnMap(true);
    },
    [dispatch]
  );

  // If the sidepanel closes, deselect any selected items on the table
  useEffect(() => {
    if (indeNodeTableRef.current?.api && !sidePanelIsOpen) {
      indeNodeTableRef.current.api.clearFocusedCell();
    }
  }, [sidePanelIsOpen]);

  return (
    <IndeNodeMapContainer>
      <TableContainer>
        <Table
          bigTitle="All Independent Nodes"
          ref={indeNodeTableRef}
          rowData={indeNodes}
          columnDefs={indeNodeColumnDefs}
          width="500px"
          height="500px"
          onRowClicked={onSelectIndeNode}
          requiresSelectedOnMap={true}
          selectedOnMap={selectedOnMap}
        />
      </TableContainer>
      <TreeContainer>
        <Tree
          onRef={onRef}
          resourceTab={RESOURCE_TYPES.INDENODE}
          resourceId={selectedIndeNode}
        />
      </TreeContainer>
    </IndeNodeMapContainer>
  );
};

export default IndeNodeMap;
